/**
 * DecisionFlow — API Client
 * Stage 8 Job A · A.2
 * S902: OI-S899-AUTH-STORAGE-GAP FIXED.
 *   - setGlobalAuthExpiredHandler() exported so AuthContext can wire a
 *     global session-expired callback. Any 401 after failed refresh calls
 *     this handler — clears session and redirects without requiring every
 *     screen to use useApiCall().
 *   - 503 retry: transient backend unavailability (DB blip) retried once
 *     after 800ms on non-auth endpoints. Auth endpoints not retried.
 *
 * Thin fetch wrapper. Key responsibilities:
 *   - Always send credentials: "include" so the httpOnly df_session cookie
 *     is attached to every request (D7 primary auth).
 *   - Parse the canonical error envelope from api_contract §2.1.
 *   - On 401 from any protected endpoint: try one refresh, retry once,
 *     then propagate as AuthExpiredError so the UI can route to /login.
 *
 * All UI code interacts with the backend via apiGet / apiPost / apiPut /
 * apiDelete — never raw fetch().
 */

const BASE = "";

export class ApiError extends Error {
  constructor(status, code, message, detail, requestId) {
    super(message || `API error (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.requestId = requestId;
  }
}

export class AuthExpiredError extends ApiError {
  constructor(message, requestId) {
    super(401, "AUTH_TOKEN_EXPIRED", message, null, requestId);
    this.name = "AuthExpiredError";
  }
}

async function parseErrorResponse(res) {
  try {
    const body = await res.json();
    const envelope = body?.detail?.error || body?.error || null;
    if (envelope) {
      return {
        code: envelope.code || "UNKNOWN",
        message: envelope.message || "",
        detail: envelope.detail ?? null,
        requestId: envelope.request_id || null,
      };
    }
    return { code: "UNKNOWN", message: JSON.stringify(body), detail: null, requestId: null };
  } catch {
    return { code: "UNKNOWN", message: res.statusText || "", detail: null, requestId: null };
  }
}

// S902 — global auth-expired handler set by AuthContext on mount.
// Called when a 401 cannot be recovered by refresh, so any screen's
// fetch can trigger session clear + redirect without useApiCall() wrapping.
let _globalAuthExpiredHandler = null;
export function setGlobalAuthExpiredHandler(fn) {
  _globalAuthExpiredHandler = fn;
}

let _refreshInFlight = null;

async function tryRefresh() {
  if (!_refreshInFlight) {
    _refreshInFlight = fetch(`${BASE}/api/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        setTimeout(() => { _refreshInFlight = null; }, 50);
      });
  }
  return _refreshInFlight;
}

// S902 — sleep helper for 503 retry backoff.
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function apiCall(method, path, body = null, { retryOn401 = true, retryOn503 = true } = {}) {
  const opts = {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  };
  if (body !== null && body !== undefined) {
    opts.body = JSON.stringify(body);
  }

  let res = await fetch(`${BASE}${path}`, opts);

  // S902 — 503 retry: transient backend unavailability (DB blip, cold start).
  // One retry after 800ms. Auth endpoints excluded — a 503 on /auth/* is
  // handled by the caller (AuthContext boot probe has its own retry loop).
  if (res.status === 503 && retryOn503 && !path.startsWith("/api/v1/auth/")) {
    await sleep(800);
    res = await fetch(`${BASE}${path}`, opts);
  }

  // 401 handling — try refresh then retry once.
  if (res.status === 401 && retryOn401 && !path.startsWith("/api/v1/auth/")) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await fetch(`${BASE}${path}`, opts);
    }
  }

  if (!res.ok) {
    const err = await parseErrorResponse(res);
    if (res.status === 401 && retryOn401) {
      const expiredErr = new AuthExpiredError(err.message, err.requestId);
      // S902 — fire global handler so session clears even without useApiCall().
      if (_globalAuthExpiredHandler) {
        _globalAuthExpiredHandler();
      }
      throw expiredErr;
    }
    throw new ApiError(res.status, err.code, err.message, err.detail, err.requestId);
  }

  if (res.status === 204) return null;

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }
  return res.text();
}

export const apiGet    = (path)          => apiCall("GET",    path);
export const apiPost   = (path, body)    => apiCall("POST",   path, body ?? {});
export const apiPatch  = (path, body)    => apiCall("PATCH",  path, body ?? {});
export const apiPut    = (path, body)    => apiCall("PUT",    path, body ?? {});
export const apiDelete = (path)          => apiCall("DELETE", path);

export async function apiLogin(email, password) {
  return apiCall("POST", "/api/v1/auth/login", { email, password }, { retryOn401: false, retryOn503: false });
}

export async function apiLogout() {
  try {
    await apiCall("POST", "/api/v1/auth/logout", {}, { retryOn401: false, retryOn503: false });
  } catch {
    // Ignore — user is signing out.
  }
}

export async function apiMe() {
  // retryOn401: false — a 401 on /me means no session, not an expired token.
  // retryOn503: false — AuthContext boot probe has its own retry loop with
  //             smarter backoff; don't double-retry here.
  return apiCall("GET", "/api/v1/auth/me", null, { retryOn401: false, retryOn503: false });
}
